import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Item } from "@/types/purchase";
import { logError } from "@/lib/sentry";
import { getProductCatalog, upsertProductCatalogItem, deleteProductCatalogItem, clearProductCatalog } from "@/supabase/product/repository/productCatalogRepository";

export function useProductCatalog(userId: string | null | undefined) {
  const [productCatalog, setProductCatalog] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // stale 응답 가드(PR #164 봇 P2 수용): 실패 롤백·catch 경로의 fetchCatalog는 이전 클로저에서
  // 늦게 불릴 수 있다 — 소유자 선검사(이전 계정 클로저는 seq를 건드리지도 못하게) 후
  // 시퀀스(최신 요청만 상태 커밋)를 공유 함수 한 곳에 둬서 upsert/delete/clear 모든 catch
  // 경로를 함께 지킨다. ref 갱신은 useLayoutEffect — 버려진 렌더가 소유권을 바꾸지 못하게.
  const fetchSeqRef = useRef(0);
  const activeUserIdRef = useRef(userId);
  useLayoutEffect(() => {
    activeUserIdRef.current = userId;
  });

  const fetchCatalog = useCallback(async () => {
    if (userId !== activeUserIdRef.current) return;
    const seq = ++fetchSeqRef.current;
    if (!userId) {
      setProductCatalog([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const catalog = await getProductCatalog(userId);
      if (seq !== fetchSeqRef.current || userId !== activeUserIdRef.current) return;
      setProductCatalog(catalog);
    } catch (error) {
      // 에러는 이미 repository에서 로깅됨
      if (seq !== fetchSeqRef.current || userId !== activeUserIdRef.current) return;
      setProductCatalog([]); // 에러 발생 시 카탈로그를 비웁니다.
    } finally {
      if (seq === fetchSeqRef.current && userId === activeUserIdRef.current) setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const addOrUpdateItemInCatalog = useCallback(async (itemToAdd: Item) => {
    if (!userId) return;

    const catalogItem = { ...itemToAdd, quantity: 1 };

    // Optimistic UI Update
    setProductCatalog(prevCatalog => {
      const existingItemIndex = prevCatalog.findIndex(
        item => item.name === catalogItem.name
      );

      if (existingItemIndex !== -1) {
        // 기존 아이템 업데이트 + 맨 앞으로 이동 — DB는 last_used_at을 갱신하므로 다음 fetch의
        // 최신순과 일치시킨다. 렌더 상한(RecentItemsSection RENDER_CAP) 도입 후에는 이동이
        // 없으면 방금 재사용한 상품이 상위 목록에 안 보이는 불일치가 생긴다.
        const updated = { ...prevCatalog[existingItemIndex], price: catalogItem.price };
        return [updated, ...prevCatalog.filter((_, index) => index !== existingItemIndex)];
      } else {
        // 새 아이템 추가 (최신순으로 맨 앞에)
        return [catalogItem, ...prevCatalog];
      }
    });

    try {
      await upsertProductCatalogItem(userId, catalogItem);
      // DB 업데이트 성공 후, 상태를 다시 동기화할 수 있습니다.
      // 여기서는 Optimistic UI 업데이트를 신뢰하고 추가적인 fetch는 하지 않습니다.
    } catch (error) {
      // 에러는 이미 repository에서 로깅됨
      logError("Failed to upsert item, UI might be out of sync.", { itemToAdd });
      // 실패 롤백: 옵티미스틱 이동/가격 변경을 DB 상태로 되돌린다 (deleteItem catch와 동일 패턴).
      fetchCatalog();
    }
  }, [userId, fetchCatalog]);

  const deleteItem = useCallback(async (itemName: string) => {
    if (!userId) return;

    // Optimistic UI Update
    setProductCatalog(prev => prev.filter(item => item.name !== itemName));

    try {
      await deleteProductCatalogItem(userId, itemName);
    } catch (error) {
      // 에러는 이미 repository에서 로깅됨
      logError("Failed to delete item, UI might be out of sync.", { itemName });
      // 실패 시 UI 롤백
      fetchCatalog();
    }
  }, [userId, fetchCatalog]);

  const clearItems = useCallback(async () => {
    if (!userId) return;

    // Optimistic UI Update
    setProductCatalog([]);

    try {
      await clearProductCatalog(userId);
    } catch (error) {
      // 에러는 이미 repository에서 로깅됨
      logError("Failed to clear items, UI might be out of sync.", {});
      // 실패 시 UI 롤백
      fetchCatalog();
    }
  }, [userId, fetchCatalog]);

  // 외부에서 카탈로그를 직접 설정해야 할 경우 (예: 벌크 임포트)
  const setCatalog = useCallback((newCatalog: Item[]) => {
    setProductCatalog(newCatalog);
    // 이 경우, DB와 동기화하는 로직을 추가할 수 있습니다.
    // 예를 들어, 모든 아이템을 upsert하는 함수를 호출할 수 있습니다.
  }, []);

  return {
    productCatalog,
    addOrUpdateItemInCatalog,
    setProductCatalog: setCatalog,
    isLoading,
    refresh: fetchCatalog, // 외부에서 카탈로그를 새로고침할 수 있는 함수
    deleteItem,
    clearItems,
  };
}